/**
 * The town's five workshops, as the HUD describes them.
 *
 * Pure, so the whole table is testable without rendering: the name, the domain, the
 * icon and — the part that matters — **what each one cannot do yet**.
 *
 * A station's panel offers three things in the design (`HUB-ZONES-SCRAP-UI.md`
 * §Workshop Interaction Flow): buy, scrap and craft. Only **scrap** exists. Vendor
 * stock, gold and recipes do not, so the other two render a designed empty state
 * naming what is missing rather than a shop with nothing in it — the rule the whole
 * HUD follows, and the reason the rebirth modal shows no ledger instead of a plausible
 * one.
 */

import { RARITY_ORDER, type ItemRarity } from '../net/protocol';
import type { StationKind } from '../net/protocol';
import type { IconName } from './icons';

export interface StationInfo {
  kind: StationKind;
  /** What the sign over the door says. */
  name: string;
  /** The HUD icon, from `ui/icons.ts` — the only place an icon is named. */
  icon: IconName;
  /** The material discipline it covers, from the spec's table. */
  domain: string;
  /**
   * What this station's own materials would be. Written down because the design's
   * scrap yields are per-discipline — a plate chest broken at the Forge should give
   * iron, a robe at the Atelier weave fibre — and **none of that exists**: scrap is
   * one generic currency. Kept here so the wording is ready when refining lands, and
   * so the panel can say plainly that it is not what happens today.
   */
  materials: string;
}

/**
 * In the order the town lays them out, which is also the order of the icon row.
 *
 * Fixed, like the hub itself: the premise of the town is that it has no navigation
 * friction, so a player learns where the Forge is once and the row never reshuffles.
 */
export const STATIONS: readonly StationInfo[] = [
  {
    kind: 'FORGE',
    name: 'The Forge',
    icon: 'forge',
    domain: 'Heavy weapons and plate armour',
    materials: 'Iron scrap, refined ingots, ore',
  },
  {
    kind: 'TANNERY',
    name: 'The Tannery',
    icon: 'tannery',
    domain: 'Bows, daggers and leather armour',
    materials: 'Raw hides, cured strips, tendons',
  },
  {
    kind: 'ATELIER',
    name: 'The Atelier',
    icon: 'atelier',
    domain: 'Staves, wands and cloth armour',
    materials: 'Weave fibre, arcane thread',
  },
  {
    kind: 'WOODWORKS',
    name: 'The Woodworks',
    icon: 'woodworks',
    domain: 'Shields, bow bases and hafts',
    materials: 'Hardwood, softwood, resins',
  },
  {
    kind: 'ARCANE_VAULT',
    name: 'The Arcane Vault',
    icon: 'vault',
    domain: 'Amulets, rings, belts and infusions',
    materials: 'Gem shards, essence dust, herbal extracts',
  },
  {
    kind: 'STASH',
    name: 'The Stash',
    icon: 'stash',
    domain: 'Everything you are not carrying',
    // It refines nothing. Said plainly rather than left blank, because the field is what
    // every other entry uses to describe a craft and a stash has none.
    materials: 'Nothing — it keeps, it does not make',
  },
];

/** One station's description, or undefined for a kind the client does not know. */
export function stationInfo(kind: string): StationInfo | undefined {
  return STATIONS.find((s) => s.kind === kind);
}

/**
 * The value of a selection, and how much of it is actually scrappable.
 *
 * Pure, and it exists because the panel must be able to say "9 of 12 — 214 scrap"
 * *before* the player commits. A locked item contributes nothing and is counted
 * separately: the server skips it rather than failing the batch, so a total that
 * silently included it would over-promise.
 *
 * `sellValue` comes from the server per carried item. It is never computed here —
 * the prices live in `configs/economy.yaml` and the client has no copy.
 */


/**
 * Which of a bag's items a "select all" should take.
 *
 * Locked items are left out rather than selected-and-skipped: the padlock is the
 * player's own instruction, and a bulk button that ticks them anyway makes the
 * safety feature look broken even though the server would honour it.
 */
export function selectableUuids(
  items: readonly { uuid: string; isLocked: boolean; sellValue?: number }[],
): string[] {
  return items
    .filter((item) => !item.isLocked && typeof item.sellValue === 'number')
    .map((item) => item.uuid);
}



/**
 * The reroll countdown, as the panel says it.
 *
 * Whole minutes above a minute and whole seconds below, because the number is a *plan*
 * rather than a measurement: "in 12 min" is what decides whether to dive again, and
 * "in 12:04" implies a precision nobody acts on. Under a minute it becomes seconds, where
 * the difference does start to matter.
 */
export function formatReroll(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return 'a moment';
  if (seconds < 60) return `${Math.ceil(seconds)}s`;
  return `${Math.ceil(seconds / 60)} min`;
}

/**
 * The uuids a "sell all X and below" button should name.
 *
 * **This replaced `trashUuids`**, which took a rarity *floor* and named everything below it. Its only
 * caller was a `Sell trash` button whose floor was Rare — i.e. exactly the set the `Magic` ceiling
 * names here — so keeping both would have been two functions and two buttons with different names and
 * identical behaviour. Deleted rather than left for a future caller: the ordering it encoded lives in
 * `RARITY_ORDER`, so nothing was lost but a second way to say the same thing.
 *
 * Asked for directly: *a sell-by-rarity button (all normal, all magic, all rare, all uniques), and it
 * sells the rarity under it as well.* So "all rare" means Normal, Magic **and** Rare — a ceiling
 * rather than `trashUuids`' floor.
 *
 * A **ceiling**, because that is how a player thinks about clearing a bag — *get rid of anything up
 * to Rare* — and it makes each button's outcome readable from its own label without consulting the one
 * beside it.
 *
 * Honours the padlock and skips anything with no sell value, exactly as the other two do — the lock is
 * the player's guard against a bulk button, and the server re-checks it regardless.
 */
export function sellableUpTo(
  items: readonly { uuid: string; isLocked: boolean; rarity: ItemRarity; sellValue?: number }[],
  ceiling: ItemRarity,
): string[] {
  const top = RARITY_ORDER.indexOf(ceiling);
  return items
    .filter(
      (item) =>
        !item.isLocked &&
        typeof item.sellValue === 'number' &&
        RARITY_ORDER.indexOf(item.rarity) <= top,
    )
    .map((item) => item.uuid);
}

/** What a rarity ceiling would fetch, in gold, for the button's own label. */
export function sellableValueUpTo(
  items: readonly { uuid: string; isLocked: boolean; rarity: ItemRarity; sellValue?: number }[],
  ceiling: ItemRarity,
): number {
  const chosen = new Set(sellableUpTo(items, ceiling));
  return items
    .filter((item) => chosen.has(item.uuid))
    .reduce((sum, item) => sum + (item.sellValue ?? 0), 0);
}
